/**
 * A Barnes-Hut octree over the bodies of a force layout.
 *
 * Repulsion is the expensive half of a force layout and it is all-pairs by definition, so at any
 * interesting size the naive loop is what decides the answer: 600 nodes is 180,000 pairs per
 * iteration and a few hundred iterations, which is a visible freeze. Approximating a distant
 * cluster by its center of mass turns that into O(n log n) and is indistinguishable on screen,
 * because the error it introduces is smallest exactly where the picture is least sensitive - far
 * away.
 *
 * Flat typed arrays rather than cell objects. The tree is rebuilt every iteration, so allocation is
 * the cost that matters rather than traversal elegance, and one growable set of buffers reused
 * across iterations allocates nothing after the first.
 */

/** Children per cell. */
const OCTANTS = 8;

/**
 * Depth at which subdivision stops.
 *
 * Coincident bodies would otherwise subdivide forever - two nodes at the same position never
 * separate into different octants, and they are the normal case rather than the exotic one, since
 * every layout starts from a seeded distribution that can repeat. At the limit a cell holds both
 * and the direct-force path handles them, where the minimum-distance floor keeps the force finite.
 */
const MAXIMUM_DEPTH = 20;

export type Octree = {
  /** Cell center and half-extent, so a traversal can judge a cell's angular size. */
  centerX: Float32Array;
  centerY: Float32Array;
  centerZ: Float32Array;
  halfSize: Float32Array;
  /** Total charge in the cell and its charge-weighted center. */
  mass: Float32Array;
  massX: Float32Array;
  massY: Float32Array;
  massZ: Float32Array;
  /** Child cell indices, `OCTANTS` per cell, `-1` where empty. */
  children: Int32Array;
  /**
   * Whether any child slot is filled.
   *
   * Carried rather than derived, because the insertion path asks it for every cell it descends
   * through and the alternative is scanning eight slots to learn one bit.
   */
  hasChildren: Uint8Array;
  /** The body in a leaf, or `-1` for an empty or internal cell. */
  bodyIndex: Int32Array;
  cellCount: number;
};

const createBuffers = (capacity: number): Octree => ({
  centerX: new Float32Array(capacity),
  centerY: new Float32Array(capacity),
  centerZ: new Float32Array(capacity),
  halfSize: new Float32Array(capacity),
  mass: new Float32Array(capacity),
  massX: new Float32Array(capacity),
  massY: new Float32Array(capacity),
  massZ: new Float32Array(capacity),
  children: new Int32Array(capacity * OCTANTS),
  hasChildren: new Uint8Array(capacity),
  bodyIndex: new Int32Array(capacity),
  cellCount: 0,
});

/**
 * Holds the buffers across rebuilds.
 *
 * A layout builds one tree per iteration and throws it away. Reallocating nine typed arrays a few
 * hundred times hands the garbage collector more work than the forces themselves cost.
 */
export type OctreeBuilder = {
  build: (
    positions: Float32Array,
    charges: Float32Array,
    bodyCount: number
  ) => Octree;
};

export const createOctreeBuilder = (): OctreeBuilder => {
  /* Grown on demand and never shrunk. Cells peak at a few times the body count for a spread-out
     graph, and re-growing every iteration would defeat the reuse. */
  let tree = createBuffers(64);

  const ensureCapacity = (cells: number): void => {
    const capacity = tree.bodyIndex.length;
    if (cells <= capacity) return;

    const grown = createBuffers(Math.max(cells, capacity * 2));
    grown.cellCount = tree.cellCount;
    grown.centerX.set(tree.centerX);
    grown.centerY.set(tree.centerY);
    grown.centerZ.set(tree.centerZ);
    grown.halfSize.set(tree.halfSize);
    grown.mass.set(tree.mass);
    grown.massX.set(tree.massX);
    grown.massY.set(tree.massY);
    grown.massZ.set(tree.massZ);
    grown.children.set(tree.children);
    grown.hasChildren.set(tree.hasChildren);
    grown.bodyIndex.set(tree.bodyIndex);
    tree = grown;
  };

  const addCell = (
    centerX: number,
    centerY: number,
    centerZ: number,
    halfSize: number
  ): number => {
    ensureCapacity(tree.cellCount + 1);
    const cell = tree.cellCount;
    tree.cellCount += 1;

    tree.centerX[cell] = centerX;
    tree.centerY[cell] = centerY;
    tree.centerZ[cell] = centerZ;
    tree.halfSize[cell] = halfSize;
    tree.mass[cell] = 0;
    tree.massX[cell] = 0;
    tree.massY[cell] = 0;
    tree.massZ[cell] = 0;
    tree.bodyIndex[cell] = -1;
    tree.hasChildren[cell] = 0;
    tree.children.fill(-1, cell * OCTANTS, cell * OCTANTS + OCTANTS);

    return cell;
  };

  const toOctant = (cell: number, x: number, y: number, z: number): number =>
    (x >= tree.centerX[cell] ? 1 : 0) |
    (y >= tree.centerY[cell] ? 2 : 0) |
    (z >= tree.centerZ[cell] ? 4 : 0);

  const childCell = (cell: number, octant: number): number => {
    const slot = cell * OCTANTS + octant;
    const existing = tree.children[slot];
    if (existing !== -1) return existing;

    const half = tree.halfSize[cell] / 2;
    const created = addCell(
      tree.centerX[cell] + ((octant & 1) === 0 ? -half : half),
      tree.centerY[cell] + ((octant & 2) === 0 ? -half : half),
      tree.centerZ[cell] + ((octant & 4) === 0 ? -half : half),
      half
    );
    /* Re-read through `tree`: `addCell` may have grown the buffers, which replaces every array. */
    tree.children[slot] = created;
    tree.hasChildren[cell] = 1;
    return created;
  };

  const insert = (
    body: number,
    x: number,
    y: number,
    z: number,
    charge: number,
    positions: Float32Array,
    charges: Float32Array
  ): void => {
    let cell = 0;
    let depth = 0;

    for (;;) {
      /* Accumulated on the way down rather than in a second bottom-up pass: every cell on the path
         contains this body by construction, so one traversal does both jobs. */
      tree.mass[cell] += charge;
      tree.massX[cell] += x * charge;
      tree.massY[cell] += y * charge;
      tree.massZ[cell] += z * charge;

      const occupant = tree.bodyIndex[cell];

      if (occupant === -1 && tree.hasChildren[cell] === 0) {
        tree.bodyIndex[cell] = body;
        return;
      }

      if (depth >= MAXIMUM_DEPTH) {
        /* At the floor the cell keeps whichever body arrived first and the rest are simply not
           indexed. Their charge is still in the cell's mass, so they repel through the
           approximation - they just stop generating exact pairs against each other, which at this
           depth means bodies closer together than the layout can resolve anyway. */
        return;
      }

      if (occupant !== -1) {
        /* A leaf becoming internal pushes its occupant down first, so the invariant "a cell has a
           body or children, never both" holds before this body descends. */
        tree.bodyIndex[cell] = -1;
        const occupantOctant = toOctant(
          cell,
          positions[occupant * 3],
          positions[occupant * 3 + 1],
          positions[occupant * 3 + 2]
        );
        const target = childCell(cell, occupantOctant);
        const occupantCharge = charges[occupant];

        tree.mass[target] += occupantCharge;
        tree.massX[target] += positions[occupant * 3] * occupantCharge;
        tree.massY[target] += positions[occupant * 3 + 1] * occupantCharge;
        tree.massZ[target] += positions[occupant * 3 + 2] * occupantCharge;
        tree.bodyIndex[target] = occupant;
      }

      cell = childCell(cell, toOctant(cell, x, y, z));
      depth += 1;
    }
  };

  return {
    build: (positions, charges, bodyCount) => {
      tree.cellCount = 0;
      if (bodyCount === 0) {
        addCell(0, 0, 0, 1);
        return tree;
      }

      let minimum = Infinity;
      let maximum = -Infinity;
      for (let index = 0; index < bodyCount * 3; index++) {
        minimum = Math.min(minimum, positions[index]);
        maximum = Math.max(maximum, positions[index]);
      }

      /* One cube for all three axes, centerd on the bodies' own bounds. A per-axis fit would make
         cells non-cubic, and the opening-angle test compares one half-extent against a distance -
         so a flattened cell would be judged far enough away when it is only far along its short
         axis. The layered layout produces exactly that shape. */
      const center = (minimum + maximum) / 2;
      const halfSize = Math.max(1e-3, (maximum - minimum) / 2) * 1.01;
      addCell(center, center, center, halfSize);

      for (let body = 0; body < bodyCount; body++) {
        insert(
          body,
          positions[body * 3],
          positions[body * 3 + 1],
          positions[body * 3 + 2],
          charges[body],
          positions,
          charges
        );
      }

      return tree;
    },
  };
};

/**
 * Accumulates the repulsion on one body into `forces`.
 *
 * `theta` is the opening angle: a cell whose width over its distance falls below it is summarized
 * by its center of mass. 0 forces exact all-pairs, which is what the agreement test uses to show
 * the approximation is the only difference between this and the naive sum.
 */
export const accumulateRepulsion = (
  tree: Octree,
  positions: Float32Array,
  body: number,
  strength: number,
  theta: number,
  minimumDistance: number,
  forces: Float32Array
): void => {
  const x = positions[body * 3];
  const y = positions[body * 3 + 1];
  const z = positions[body * 3 + 2];

  let forceX = 0;
  let forceY = 0;
  let forceZ = 0;

  /* An explicit stack, sized to the tree. Recursion here is called once per body per iteration -
     hundreds of thousands of times - and the call overhead is measurable against the arithmetic. */
  const stack: number[] = [0];

  while (stack.length > 0) {
    const cell = stack.pop();
    if (cell === undefined) break;

    const mass = tree.mass[cell];
    if (mass === 0) continue;

    const occupant = tree.bodyIndex[cell];
    const centerX = tree.massX[cell] / mass;
    const centerY = tree.massY[cell] / mass;
    const centerZ = tree.massZ[cell] / mass;

    const deltaX = x - centerX;
    const deltaY = y - centerY;
    const deltaZ = z - centerZ;
    const distance = Math.max(
      minimumDistance,
      Math.hypot(deltaX, deltaY, deltaZ)
    );

    const isLeaf = occupant !== -1;
    /* The body's own leaf contributes nothing: a body does not repel itself, and the delta would
       be exactly zero. */
    if (isLeaf && occupant === body) continue;

    if (isLeaf || tree.halfSize[cell] * 2 < theta * distance) {
      /* Inverse square, so the force falls off the way a charge does. Divided by distance a third
         time to turn the delta into a unit vector without a second square root. */
      const magnitude = (strength * mass) / (distance * distance * distance);
      forceX += deltaX * magnitude;
      forceY += deltaY * magnitude;
      forceZ += deltaZ * magnitude;
      continue;
    }

    for (let octant = 0; octant < OCTANTS; octant++) {
      const child = tree.children[cell * OCTANTS + octant];
      if (child !== -1) stack.push(child);
    }
  }

  forces[body * 3] += forceX;
  forces[body * 3 + 1] += forceY;
  forces[body * 3 + 2] += forceZ;
};
